import Vue from 'vue';
import { db } from '@/firebase';
import { get$bindFirestoreOptions } from './VuexFirestoreBindingsMixin';
import {
	CompanyTeamsQueryDescription,
	CompanyTeamsQueryWhere,
	describeCompanyTeamsQuery,
} from './companyTeamsQuery';

/**
 * Owns the live binding of a company's teams so it exists once. The
 * company-role Add dialog offers them in its team picker.
 *
 * Expects the host component to also use TeamUserMixin, which supplies `user`
 * and `hasManageTeamsPermission`.
 */
export const CompanyTeamsBindingMixin = Vue.extend({
	data () {
		return {
			loadingAllTeams: false,
			allTeams: [],
		};
	},
	methods: {
		// Stop listening to the previous company's teams, then show nothing
		clearCompanyTeams () {
			if (this._firestoreUnbinds && this._firestoreUnbinds['allTeams']) {
				this.$unbind('allTeams');
			}
			this.allTeams = [];
			// $unbind cancels the listener without settling a $bind that is still in flight, so
			// that call's `finally` never runs: clear the flag here or the cards stay skeletons
			this.loadingAllTeams = false;
		},
		// The company's active teams, as this viewer may read them
		async loadCompanyTeams (companyId: string | null) {
			if (!companyId) {
				this.clearCompanyTeams();
				return;
			}

			this.loadingAllTeams = true;
			try {
				const description: CompanyTeamsQueryDescription | null = describeCompanyTeamsQuery({
					companyId,
					canManageTeams: !!this.hasManageTeamsPermission,
					viewerUserId: this.hasManageTeamsPermission ? null : this.user.id,
				});
				if (!description) {
					// Nothing to query is the same outcome as no company, never a TypeError below
					this.clearCompanyTeams();
					return;
				}
				let teamsRef = db
					.collection(description.collection)
					.limit(description.limit);
				description.where.forEach(([field, op, value]: CompanyTeamsQueryWhere) => {
					teamsRef = teamsRef.where(field, op, value);
				});
				await this.$bind(
					'allTeams',
					teamsRef,
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error loading teams:', error);
			} finally {
				this.loadingAllTeams = false;
			}
		},
	},
});
